// ─────────────────────────────────────────────────────────────────────────────
// AVISOS POR CORREO (EmailJS)
//
// Manda un correo cuando se crea una orden de pedido y otro cuando se
// autoriza. Nada más: son los dos momentos en que alguien de fuera del
// sistema necesita enterarse.
//
// ── POR QUÉ NO SE USA LA LIBRERÍA `@emailjs/browser` ────────────────────────
// Este proyecto se mantiene copiando archivos a Magic Patterns, y Magic
// Patterns NO instala dependencias de npm: en cuanto un archivo importa un
// paquete que no está, el build entero se cae. Así que se llama directo a la
// API de EmailJS con `fetch`, que es exactamente lo que hace la librería por
// dentro. No hay nada que instalar.
//
// ── QUÉ HAY QUE CONFIGURAR ANTES DE QUE FUNCIONE ────────────────────────────
// Ver emailjs/COMO-CONFIGURAR.md. En resumen: crear la cuenta, conectar el
// correo desde el que sale, pegar la plantilla y copiar aquí abajo los tres
// identificadores. Mientras estén en blanco, la app no intenta enviar nada y
// todo lo demás sigue funcionando igual.
// ─────────────────────────────────────────────────────────────────────────────

import type { Pedido } from './types';
import { EMPRESA, etiquetaTipo, fmtFecha, fmtGal, suplidorDe, urlVerificacion } from './modules/pedidoDocumento';

// ── 1. LOS TRES IDENTIFICADORES DE EMAILJS ──────────────────────────────────
// Salen del panel de EmailJS. Son públicos por diseño (viajan en el navegador
// de todo el que abra la app); lo que impide que otro los use es la lista de
// dominios permitidos que se configura en la cuenta. Ver el instructivo.
export const EMAILJS_SERVICE_ID: string = '';
export const EMAILJS_TEMPLATE_ID: string = '';
export const EMAILJS_PUBLIC_KEY: string = '';

// ── 2. A QUIÉN LE LLEGA ─────────────────────────────────────────────────────
// Separados por coma. Se puede poner más de uno.
//
// Ojo con quién va aquí: al autorizar, el correo lleva el enlace a la copia
// digital de la orden, que abre sin pedir contraseña. Es lo que se busca —que
// el suplidor pueda cotejarla— pero significa que quien reciba el correo la
// puede ver.
export const CORREOS_AVISO: string = 'info.junquitogas@gmail.com';

// Se puede avisar a otra gente solo cuando la orden queda autorizada (por
// ejemplo al suplidor), dejando la creación como aviso interno. En blanco,
// los dos avisos van a la misma lista de arriba.
export const CORREOS_AVISO_AUTORIZADA: string = '';
const API = 'https://api.emailjs.com/api/v1.0/email/send';

// Sin los tres identificadores no hay nada que intentar. Se comprueba antes de
// cada envío para que la app funcione igual mientras no esté configurado: sin
// errores en pantalla y sin bloquear el guardado.
export const correoConfigurado = () => !!(EMAILJS_SERVICE_ID && EMAILJS_TEMPLATE_ID && EMAILJS_PUBLIC_KEY);
export type EventoCorreo = 'creada' | 'autorizada';
export interface ResultadoCorreo {
  enviado: boolean;
  motivo?: string;
  destinatarios?: string;
}

// El detalle de la orden en texto plano, una línea por destino. Va como texto
// y no como tabla HTML porque el valor viaja como variable de la plantilla, y
// EmailJS escapa lo que se le pasa: cualquier etiqueta llegaría escrita tal
// cual en el correo. La plantilla lo pinta respetando los saltos de línea.
function detalleLineas(p: Pedido): string {
  const lineas = (p.lineas ?? []).filter(l => l.destino || l.galones > 0);
  if (!lineas.length) return 'Sin destinos indicados.';
  return lineas.map(l => {
    const rnc = l.rncDestino ? `  ·  RNC ${l.rncDestino}` : '';
    return `${l.destino || 'Sin destino'}  —  ${fmtGal(l.galones)} galones${rnc}`;
  }).join('\n');
}
const totalGalones = (p: Pedido) => (p.lineas ?? []).reduce((s, l) => s + (l.galones || 0), 0);

// Lo que ve quien abre el correo. Se arma aquí y no en la plantilla de EmailJS
// para que el texto viva en el repositorio: así se revisa, se corrige y queda
// en el historial, en vez de estar escondido en un panel web.
function variables(p: Pedido, evento: EventoCorreo, quien: string) {
  const creada = evento === 'creada';
  const suplidor = suplidorDe(p.tipo);

  // El botón del correo siempre lleva a algún sitio, y a propósito: no se sabe
  // si EmailJS admite bloques condicionales en la plantilla, así que la
  // plantilla no los usa. Con la orden autorizada el enlace es su copia
  // digital (la misma que abre el QR impreso); con la orden recién creada,
  // que todavía no tiene token, lleva al sistema.
  let enlace = '';
  let enlaceTexto = 'Abrir el sistema';
  let enlaceNota = 'Cuando la orden se autorice llegará otro aviso con el enlace a su copia digital.';
  try {
    const {
      origin,
      pathname
    } = window.location;
    enlace = origin + pathname.replace(/\/$/, '') + '/';
    if (p.token) {
      enlace = urlVerificacion(p.token);
      enlaceTexto = 'Ver la copia digital de la orden';
      enlaceNota = 'Es la misma que abre el código QR impreso en el formulario.';
    }
  } catch {

    /* fuera del navegador no hay window.location: el correo sale sin enlace */}
  return {
    to_email: (creada ? CORREOS_AVISO : CORREOS_AVISO_AUTORIZADA || CORREOS_AVISO).trim(),
    reply_to: EMPRESA.email,
    asunto: creada ? `Nueva orden de pedido ${p.numeroOrden} · ${suplidor.corta}` : `Orden de pedido ${p.numeroOrden} AUTORIZADA · ${suplidor.corta}`,
    titulo: creada ? 'Nueva orden de pedido' : 'Orden de pedido autorizada',
    subtitulo: creada ? 'Se registró una orden nueva. Todavía está en borrador: no se ha firmado.' : 'La orden quedó firmada y ya se puede despachar.',
    // La plantilla usa este color en la barra de arriba y en el número.
    color: creada ? '#B45309' : '#15803D',
    estado: creada ? 'BORRADOR — pendiente de autorizar' : 'AUTORIZADA',
    numero_orden: p.numeroOrden || '—',
    suplidor: etiquetaTipo(p.tipo),
    destinatario: suplidor.razonSocial,
    fecha: fmtFecha(p.fecha) || p.fecha || '—',
    conductor: p.conductor || '—',
    pase_chofer: p.paseChofer || '—',
    ficha_cola: p.fichaCola || '—',
    placa_camion: p.placaCamion || '—',
    lineas: detalleLineas(p),
    total_galones: `${fmtGal(totalGalones(p))} galones`,
    observaciones: p.observaciones?.trim() || '—',
    responsable_rotulo: creada ? 'Creada por' : 'Autorizada por',
    responsable: quien || '—',
    enlace,
    enlace_texto: enlaceTexto,
    enlace_nota: enlaceNota,
    empresa: EMPRESA.nombre,
    empresa_rnc: EMPRESA.rnc,
    empresa_telefono: EMPRESA.telefono,
    empresa_email: EMPRESA.email
  };
}

/**
 * Manda el aviso. NUNCA lanza: si algo falla devuelve `enviado: false` con el
 * motivo, porque un correo que no sale no puede impedir que la orden se
 * guarde. La orden es el documento; el correo es solo el aviso.
 */
export async function enviarAvisoPedido(p: Pedido, evento: EventoCorreo, quien: string): Promise<ResultadoCorreo> {
  if (!correoConfigurado()) {
    return {
      enviado: false,
      motivo: 'EmailJS no está configurado (faltan los identificadores en src/email.ts).'
    };
  }
  const params = variables(p, evento, quien);
  if (!params.to_email) {
    return {
      enviado: false,
      motivo: 'No hay ningún correo de destino configurado.'
    };
  }
  try {
    const r = await fetch(API, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        service_id: EMAILJS_SERVICE_ID,
        template_id: EMAILJS_TEMPLATE_ID,
        user_id: EMAILJS_PUBLIC_KEY,
        template_params: params
      })
    });
    if (!r.ok) {
      // EmailJS contesta con el motivo en texto plano, no en JSON.
      const texto = await r.text().catch(() => '');
      return {
        enviado: false,
        motivo: `EmailJS respondió ${r.status}${texto ? `: ${texto}` : ''}`
      };
    }
    return {
      enviado: true,
      destinatarios: params.to_email
    };
  } catch (e) {
    return {
      enviado: false,
      motivo: e instanceof Error ? e.message : 'sin conexión'
    };
  }
}